import axios from 'axios';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import DataTable from '../components/DataTable';

const ListarObligaciones = () => {
  const { id } = useParams(); // ID del contrato
  const [obligaciones, setObligaciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Obtener obligaciones por contrato
  const getData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`/api/obligaciones/contrato/${id}`);

      if (response.data.ok) {
        const data = (response.data.obligaciones || []).map((item, index) => ({
          numero: index + 1,
          observacion: item.observacion,
          _id: item._id,

          funciones: (
            <div className="d-flex gap-2 flex-wrap justify-content-end">
              <button
                className="btn btn-danger btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                title="Eliminar"
                type="button"
                onClick={() => deleteObservacion(item._id)}
              >
                <i className="fa-solid fa-trash"></i>
              </button>
            </div>
          ),
        }));

        setObligaciones(data);
      } else {
        setObligaciones([]);
      }
    } catch (error) {
      console.error('Error al cargar las obligaciones:', error.message);
      setObligaciones([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    if (id) getData();
  }, [id, getData]);

  const deleteObservacion = async (idObligacion) => {
    try {
      Swal.fire({
        title: '¿Está seguro?',
        text: 'Esta obligación será eliminada permanentemente',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar',
      }).then(async (result) => {
        if (!result.isConfirmed) return;

        const { data } = await axios.delete(`/api/obligaciones/delete/${idObligacion}`);

        // Actualizar la lista de forma optimista
        setObligaciones((prev) => prev.filter((ob) => ob._id !== idObligacion));

        Swal.fire({
          icon: 'success',
          title: data.message || 'Obligación eliminada correctamente',
          showConfirmButton: false,
          timer: 1500,
        });
      });
    } catch (error) {
      console.error('Error al eliminar la obligación:', error.message);
      Swal.fire(
        'Error',
        error.response?.data?.message || 'No se pudo eliminar la obligación',
        'error'
      );
      getData(); // Intentar sincronizar la lista
    }
  };

  // Columnas
  const columns = useMemo(
    () => [
      { Header: 'N°', accessor: 'numero' },
      {
        Header: 'Obligación',
        accessor: 'observacion',
        width: 280,
        hideHeaderOnMobile: true,
      },
      { accessor: 'funciones' },
    ],
    []
  );

  return (
    <div>
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          {/* Header */}
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">Obligaciones contractuales</h5>
                <small className="text-secondary">
                  Control y gestión de obligaciones asociadas al contrato.
                </small>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={() => navigate(-1)}
                  title="Volver"
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>

                  Volver
                </button>

                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={() => getData()}
                  title="Actualizar"
                  disabled={loading}
                >
                  <i className={`fa-solid ${loading ? 'fa-spinner fa-spin' : 'fa-rotate'} me-2`}></i>
                  Actualizar
                </button>
              </div>
            </div>
          </div>

          {/* Body */}
          <div>
            {loading ? (
              <div className="text-center text-secondary py-5">
                <div className="spinner-border" role="status" aria-hidden="true"></div>
                <div className="mt-3">Cargando obligaciones…</div>
              </div>
            ) : Array.isArray(obligaciones) && obligaciones.length > 0 ? (
              <div className="overflow-hidden">
                <DataTable columns={columns} data={obligaciones} />
              </div>
            ) : (
              <div className="text-center text-secondary py-5">
                <div className="display-6 mb-2">🐾</div>
                <div className="fw-semibold">No hay obligaciones registradas</div>
                <div className="small">No se encontraron obligaciones para este contrato.</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListarObligaciones;
